// @ts-check
// ====== Audit Log ======
// Aggiunge l'entry sia in memoria (per rendering immediato) sia su Supabase.
// La scrittura DB e' fire-and-forget: se fallisce viene loggato l'errore ma
// il flusso UI non si blocca.
// Cache lato client dell'IP pubblico (best effort, una volta per sessione)
let _clientIpCache = null;
async function getClientIp() {
  if (_clientIpCache !== null) return _clientIpCache;
  try {
    // Endpoint pubblico no-auth, no-tracking. Fail silenzioso.
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const r = await fetch('https://api.ipify.org?format=json', { signal: ctrl.signal });
    clearTimeout(t);
    if (r.ok) {
      const j = await r.json();
      _clientIpCache = j.ip || '';
    } else _clientIpCache = '';
  } catch {
    _clientIpCache = '';
  }
  return _clientIpCache;
}

/**
 * Registra una voce di audit sia in memoria sia su Supabase.
 * @param {{
 *   action: 'create' | 'update' | 'delete' | 'system',
 *   entity: string,
 *   target: string,
 *   details?: string,
 *   changes?: Array<{ field: string, from: string, to: string }>
 * }} params
 * @returns {Promise<void>}
 */
function logAudit({ action, entity, target, details, changes }) {
  const profile = typeof getCurrentUser === 'function' ? getCurrentUser() : null;
  const entry = {
    id: 'a' + Date.now() + Math.random().toString(36).slice(2, 7),
    timestamp: new Date().toISOString(),
    user: (profile && profile.name) || state.currentUser || 'Sistema',
    action,
    entity,
    target,
    details: details || '',
    changes: changes || []
  };
  state.auditLog.unshift(entry);
  if (state.auditLog.length > 500) state.auditLog = state.auditLog.slice(0, 500);

  if (typeof api !== 'undefined' && api.insertAudit) {
    const ua = (navigator.userAgent || '').slice(0, 240);
    // Per i Login: aspettiamo l'IP. Per il resto fire-and-forget per non rallentare.
    if (entity === 'Sistema' && target === 'Login') {
      return getClientIp().then((ip) =>
        api.insertAudit({
          user_id: profile ? profile.id : null,
          user_name: entry.user,
          action,
          entity,
          target,
          details: entry.details,
          changes: entry.changes,
          ip: ip || null,
          user_agent: ua
        })
      );
    }
    return api.insertAudit({
      user_id: profile ? profile.id : null,
      user_name: entry.user,
      action,
      entity,
      target,
      details: entry.details,
      changes: entry.changes,
      ip: null,
      user_agent: ua
    });
  }
  return Promise.resolve();
}

/**
 * Calcola le differenze tra due clienti per audit log.
 * @param {Cliente} oldC
 * @param {Cliente} newC
 * @returns {Array<{ field: string, from: string, to: string }>}
 */
function diffClient(oldC, newC) {
  const changes = [];
  const labels = {
    nome: 'Nome',
    cpl: 'CPL',
    pm1: 'PM1',
    pm2: 'PM2',
    serviceLineRef: 'Riferimento Service Line',
    ruolo: 'Ruolo',
    assegnazionePerenne: 'Assegnazione perenne',
    directory: 'Directory',
    pim: 'PIM',
    note: 'Note',
    tenantId: 'Tenant ID'
  };
  for (const f of Object.keys(labels)) {
    const a = (oldC[f] || '').toString().trim();
    const b = (newC[f] || '').toString().trim();
    if (a !== b) changes.push({ field: labels[f], from: a, to: b });
  }
  const oldPer = (oldC.perimetri || []).slice().sort().join(', ');
  const newPer = (newC.perimetri || []).slice().sort().join(', ');
  if (oldPer !== newPer) changes.push({ field: 'Perimetri', from: oldPer, to: newPer });

  const oldTec = (oldC.tecnici || []).slice().sort().join(', ');
  const newTec = (newC.tecnici || []).slice().sort().join(', ');
  if (oldTec !== newTec) changes.push({ field: 'Tecnici', from: oldTec, to: newTec });

  const allRes = new Set([...Object.keys(oldC.accessi || {}), ...Object.keys(newC.accessi || {})]);
  allRes.forEach((r) => {
    const a = oldC.accessi?.[r] || '';
    const b = newC.accessi?.[r] || '';
    if (a !== b) changes.push({ field: `Accesso · ${r}`, from: a, to: b });
  });

  const contrLabels = {
    numero: 'Contratto · Numero',
    tipo: 'Contratto · Tipo',
    inizio: 'Contratto · Inizio',
    fine: 'Contratto · Fine',
    ore: 'Contratto · Ore',
    importo: 'Contratto · Importo',
    note: 'Contratto · Note'
  };
  for (const f of Object.keys(contrLabels)) {
    const a = (oldC.contratto?.[f] || '').toString().trim();
    const b = (newC.contratto?.[f] || '').toString().trim();
    if (a !== b) changes.push({ field: contrLabels[f], from: a, to: b });
  }

  const sub = [
    { key: 'referenti', label: 'Referente', titleFn: (x) => x.nome || x.email || '(senza nome)' },
    { key: 'documenti', label: 'Documento', titleFn: (x) => x.titolo || x.url || '(senza titolo)' },
    {
      key: 'scadenze',
      label: 'Scadenza',
      titleFn: (x) => `${x.titolo || '(senza titolo)'} - ${x.data || ''}`
    }
  ];
  sub.forEach(({ key, label, titleFn }) => {
    const oldArr = oldC[key] || [];
    const newArr = newC[key] || [];
    const oldIds = new Set(oldArr.map((x) => x.id));
    const newIds = new Set(newArr.map((x) => x.id));
    newArr.forEach((x) => {
      if (!oldIds.has(x.id)) changes.push({ field: label + ' aggiunto', from: '', to: titleFn(x) });
    });
    oldArr.forEach((x) => {
      if (!newIds.has(x.id)) changes.push({ field: label + ' rimosso', from: titleFn(x), to: '' });
    });
    newArr.forEach((x) => {
      const o = oldArr.find((y) => y.id === x.id);
      if (!o) return;
      if (JSON.stringify(o) !== JSON.stringify(x))
        changes.push({ field: label + ' modificato', from: titleFn(o), to: titleFn(x) });
    });
  });
  return changes;
}

function renderAuditDetails(a) {
  if (a.changes && a.changes.length) {
    const renderRow = (c) => `
      <div><strong>${escapeHtml(c.field)}:</strong>
      ${c.from ? `<span class="diff-from">${escapeHtml(c.from)}</span> →` : ''}
      <span class="diff-to">${escapeHtml(c.to || '(vuoto)')}</span></div>`;
    const VISIBLE = 5;
    const firstChunk = a.changes.slice(0, VISIBLE).map(renderRow).join('');
    const remaining = a.changes.slice(VISIBLE);
    const moreHtml = remaining.length
      ? `<details class="audit-more">
           <summary>+${remaining.length} altre modifiche</summary>
           ${remaining.map(renderRow).join('')}
         </details>`
      : '';
    return `<div class="audit-diff">${firstChunk}${moreHtml}</div>`;
  }
  return `<span class="muted">${escapeHtml(a.details || '')}</span>`;
}

function renderStoricoCliente(nome) {
  const root = $('#storicoClienteList');
  if (!nome) {
    root.innerHTML = '<p class="muted">Lo storico è disponibile dopo il salvataggio.</p>';
    return;
  }
  const entries = state.auditLog.filter((a) => a.target === nome);
  if (!entries.length) {
    root.innerHTML = '<p class="muted">Nessuna modifica registrata per questo cliente.</p>';
    return;
  }
  root.innerHTML = entries
    .slice(0, 50)
    .map(
      (a) => `
    <div class="item-card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
        <div><span class="audit-action ${a.action}">${escapeHtml(a.action)}</span> <strong>${escapeHtml(a.user)}</strong></div>
        <div class="muted" style="font-size:11px;">${escapeHtml(fmtDateTime(a.timestamp))}</div>
      </div>
      ${renderAuditDetails(a)}
    </div>`
    )
    .join('');
}

// Pagina successiva di audit log: chiede al server le N voci più vecchie
// rispetto alla più vecchia attualmente in memoria. Append a state.auditLog
// e ri-renderizza la vista.
async function loadMoreAuditLog(pageSize = 200) {
  if (!isAdmin()) return;
  const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById('btnLoadMoreAudit'));
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Caricamento…';
  }
  try {
    const oldest = state.auditLog[state.auditLog.length - 1]?.timestamp;
    let q = sb.from('audit_log').select('*').order('ts', { ascending: false }).limit(pageSize);
    if (oldest) q = q.lt('ts', oldest);
    const { data, error } = await q;
    if (error) throw error;
    const rows = (data || []).map((a) => ({
      id: a.id,
      timestamp: a.ts,
      user: a.user_name || 'Sistema',
      action: a.action,
      entity: a.entity || '',
      target: a.target || '',
      details: a.details || '',
      changes: Array.isArray(a.changes) ? a.changes : [],
      ip: a.ip || '',
      userAgent: a.user_agent || ''
    }));
    if (rows.length) state.auditLog.push(...rows);
    if (rows.length < pageSize) {
      // niente altre pagine
      if (btn) {
        btn.style.display = 'none';
      }
      toast(
        rows.length
          ? `Caricate altre ${rows.length} voci (fine storico)`
          : 'Nessuna voce più vecchia'
      );
    } else {
      toast(`Caricate altre ${rows.length} voci`);
    }
    renderStorico();
  } catch (e) {
    console.error(e);
    toast('Errore caricamento: ' + (e.message || e));
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Carica altre 200 voci';
    }
  }
}

function renderStorico() {
  if (!isAdmin()) return;
  if (!$('#tbodyStorico')) return; // vista "storico" lazy non ancora montata
  wireStoricoView();
  const cliInput = /** @type {HTMLInputElement | null} */ ($('#filterStoricoCliente'));
  const actSelect = /** @type {HTMLSelectElement | null} */ ($('#filterStoricoAction'));
  const usrSelect = /** @type {HTMLSelectElement | null} */ ($('#filterStoricoUser'));
  const cliFilter = (cliInput?.value || '').toLowerCase().trim();
  const actFilter = actSelect?.value || '';
  const usrFilter = usrSelect?.value || '';

  const users = [...new Set(state.auditLog.map((a) => a.user).filter(Boolean))].sort();
  fillSelect(usrSelect, users, 'Tutti gli utenti');

  let list = state.auditLog;
  if (cliFilter) list = list.filter((a) => (a.target || '').toLowerCase().includes(cliFilter));
  if (actFilter) list = list.filter((a) => a.action === actFilter);
  if (usrFilter) list = list.filter((a) => a.user === usrFilter);

  const tbody = $('#tbodyStorico');
  tbody.innerHTML = list.length
    ? list
        .map(
          (a) => `
    <tr>
      <td>${escapeHtml(fmtDateTime(a.timestamp))}</td>
      <td>${escapeHtml(a.user)}</td>
      <td><span class="audit-action ${a.action}">${escapeHtml(a.action)}</span> <span class="muted">${escapeHtml(a.entity)}</span></td>
      <td><strong>${escapeHtml(a.target || '')}</strong></td>
      <td>${renderAuditDetails(a)}</td>
    </tr>`
        )
        .join('')
    : '<tr><td colspan="5" style="text-align:center;padding:30px;color:var(--text-muted)">Nessuna modifica registrata.</td></tr>';
}

// Event listeners storico — wiring idempotente (la vista "storico" è lazy:
// gli elementi esistono solo dopo il mount, quindi leghiamo qui e richiamiamo
// wireStoricoView() da renderStorico, eseguito dal router dopo l'iniezione).
let _storicoWired = false;
function wireStoricoView() {
  if (_storicoWired) return;
  if (!document.getElementById('tbodyStorico')) return; // vista non ancora montata
  ['filterStoricoCliente', 'filterStoricoAction', 'filterStoricoUser'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener(el.tagName === 'INPUT' ? 'input' : 'change', renderStorico);
  });
  const btnLoadMoreAudit = document.getElementById('btnLoadMoreAudit');
  if (btnLoadMoreAudit) btnLoadMoreAudit.addEventListener('click', () => loadMoreAuditLog(200));
  const btnExportStorico = document.getElementById('btnExportStorico');
  if (btnExportStorico)
    btnExportStorico.addEventListener('click', () => {
      downloadFile(
        'storico-mase.json',
        JSON.stringify(state.auditLog, null, 2),
        'application/json'
      );
    });
  const btnClearStorico = document.getElementById('btnClearStorico');
  if (btnClearStorico)
    btnClearStorico.addEventListener('click', async () => {
      if (!isAdmin()) return;
      const n = state.auditLog.length;
      if (!n) {
        toast('Lo storico è già vuoto');
        return;
      }
      const ok = await confirmAction({
        level: 'danger',
        title: 'Svuotare lo storico',
        message: `Stai per cancellare tutto lo storico delle modifiche (${n} voce${n === 1 ? '' : 'i'}).`,
        details: [
          'Tutte le voci di audit (create/update/delete) verranno rimosse',
          'La cronologia di chi ha fatto cosa e quando andrà persa',
          'Le anagrafiche di clienti, risorse e utenti NON vengono toccate'
        ],
        warning:
          'Operazione irreversibile. Esporta prima lo storico se vuoi conservarne una copia.',
        confirmText: 'Svuota storico',
        requireType: 'ELIMINA'
      });
      if (!ok) return;
      try {
        await api.clearAuditLog();
        state.auditLog = [];
        logAudit({
          action: 'system',
          entity: 'Sistema',
          target: 'Storico',
          details: 'Storico svuotato'
        });
        notifyCritical?.('storico.cleared', { count: n });
        renderStorico();
        renderDashboard();
        toast('Storico svuotato');
      } catch (e) {
        console.error(e);
        toast('Errore svuotamento storico: ' + (e.message || e));
      }
    });
  if (typeof wireAuditServerFilters === 'function') wireAuditServerFilters();
  _storicoWired = true;
}
wireStoricoView(); // no-op finché la vista lazy non è montata

Object.assign(globalThis, {
  logAudit,
  diffClient,
  renderAuditDetails,
  renderStoricoCliente,
  renderStorico,
  loadMoreAuditLog
});
export {};
